import React, { useState, useEffect, useRef } from 'react';
import { FaArrowLeft, FaPaperPlane, FaBell, FaTimes } from "react-icons/fa";
import { supabase } from '../data/supabaseClient';
import CustomButton9, { CustomButton1, CustomButton10, CustomButton11, CustomButton2, CustomButton3, CustomButton4, CustomButton5, CustomButton6, CustomButton7, CustomButton8, CustomInputBox2, ChatInput, CloseButton2, Button } from '../Templates';
import RideActionsV2 from './RideActionsV2/RideActionsV2';
import PackageActionsV2 from './PackageActionsV2/PackageActionsV2';
import UserActionsDropdown from '../common/UserActionsDropdown';
import { format } from 'date-fns';
import { useChat } from './ChatContext';
import Spinner from '../common/Spinner';
import { useTranslation } from 'react-i18next';
import { sendNewMessageNotification } from '../Notifications/notificationService';
import './ChatV2.css';

const ChatWindow = ({ conversationId, onBack, occurrenceContext }) => {
  const { t } = useTranslation('common');
  const { toggleChat } = useChat();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [otherParticipant, setOtherParticipant] = useState(null);
  const [conversationDetails, setConversationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  // State for occurrence details (for recurring posts)
  const [occurrenceDetails, setOccurrenceDetails] = useState(null);

  const { 
    otherUnreadMessages,
    clearOtherUnreadMessage 
  } = useChat();
  
  const { 
    addNotification,
    showConversations
  } = useChat();

  // Clear unread status when entering conversation
  useEffect(() => {
    if (conversationId) {
      clearOtherUnreadMessage(conversationId);
    }
  }, [conversationId, clearOtherUnreadMessage]);

  const handleBack = () => {
    // Clear unread status for current conversation
    clearOtherUnreadMessage(conversationId);
    
    if (onBack) {
      onBack();
    }
    showConversations();
  };
  
  // Handle retry on error
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    // Re-fetch everything
    fetchUser();
  };

  // Get current user
  const fetchUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw userError;
      }
      
      setCurrentUser(user);
      
      // If user is retrieved successfully, fetch messages and participants
      if (user) {
        await fetchMessagesAndParticipants(user);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setError(t('chat.error_user_fetch'));
      setIsLoading(false);
    }
  };

  // Fetch messages and setup real-time subscription
  const fetchMessagesAndParticipants = async (user) => {
    if (!conversationId || !user) return;

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Fetching conversation data for ID:', conversationId);
      
      // Get conversation details
      const { data: convoData, error: convoError } = await supabase
        .from('conversations')
        .select(`
          id,
          title,
          posts_id,
          package_id
        `)
        .eq('id', conversationId)
        .single();

      if (convoError) {
        console.error('Error fetching conversation details:', convoError);
        throw convoError;
      }

      console.log('Conversation details fetched successfully:', convoData);
      
      // Fetch related post data if needed
      if (convoData.posts_id) {
        const { data: postData, error: postError } = await supabase
          .from('posts')
          .select('*')
          .eq('id', convoData.posts_id)
          .single();
          
        if (!postError && postData) {
          convoData.posts = postData;
        }
      }
      
      setConversationDetails(convoData);

      // Get participants separately to avoid recursion issues
      const { data: participantsData, error: participantsError } = await supabase
        .from('conversation_participants')
        .select(`
          user_id,
          profiles:user_id (id, username, avatar_url)
        `)
        .eq('conversation_id', conversationId);

      if (participantsError) {
        console.error('Error fetching participants:', participantsError);
        throw participantsError;
      }

      console.log('Participants fetched successfully:', participantsData);

      // Find other participant
      const otherParticipantData = participantsData?.find(
        p => p.user_id !== user.id
      );
      
      if (otherParticipantData?.profiles) {
        setOtherParticipant(otherParticipantData.profiles);
      }

      // Get messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select(`
          id,
          conversation_id,
          user_id,
          text,
          is_system_message,
          created_at
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Error fetching messages:', messagesError);
        throw messagesError;
      }

      // Manually add user profiles to messages to avoid RLS recursion
      const messagesWithProfiles = messagesData.map(msg => {
        const isCurrentUser = msg.user_id === user.id;
        const participantData = participantsData.find(p => p.user_id === msg.user_id);
        
        return {
          ...msg,
          is_from_current_user: isCurrentUser,
          profiles: participantData?.profiles || null
        };
      });

      console.log('Messages fetched successfully:', messagesWithProfiles.length);
      setMessages(messagesWithProfiles || []);
      
      // Mark messages as read
      // We don't have is_read field yet, so let's update the conversation to mark it as read
      // in a different way
      if (otherUnreadMessages.has(conversationId)) {
        clearOtherUnreadMessage(conversationId);
      }
      
      // Subscribe to new messages
      const channel = supabase
        .channel(`messages:${conversationId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        }, (payload) => {
          console.log('New message received via subscription:', payload.new);
          // Add profile information to the message
          const isFromCurrentUser = payload.new.user_id === user.id;
          const participantData = participantsData.find(p => p.user_id === payload.new.user_id);
          
          setMessages(prev => [...prev, {
            ...payload.new,
            is_from_current_user: isFromCurrentUser,
            profiles: participantData?.profiles || null
          }]);
        })
        .subscribe();

      console.log('Subscribed to message updates');

      // Store channel for cleanup
      return () => {
        supabase.removeChannel(channel);
      };
    } catch (error) {
      console.error('Error fetching conversation data:', error);
      setError(t('chat.error_conversation_load'));
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    let cleanup = () => {};
    
    // Initial fetch user and data
    const initialize = async () => {
      const cleanupFn = await fetchUser();
      if (typeof cleanupFn === 'function') {
        cleanup = cleanupFn;
      }
    };
    
    initialize();
    
    // Cleanup function
    return () => {
      cleanup();
    };
  }, [conversationId]);

  // Fetch occurrence details if occurrence context is provided
  useEffect(() => {
    const fetchOccurrenceDetails = async () => {
      if (!occurrenceContext?.occurrenceId) {
        setOccurrenceDetails(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('post_occurrences')
          .select('id, occurrence_date, occurrence_time, parent_post_id')
          .eq('id', occurrenceContext.occurrenceId)
          .single();

        if (error) throw error;

        setOccurrenceDetails(data);
      } catch (error) {
        console.error('Error fetching occurrence details:', error);
        setOccurrenceDetails(null);
      }
    };

    fetchOccurrenceDetails();
  }, [occurrenceContext]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    // Support both form submission and direct call
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!newMessage.trim() || !conversationId || !currentUser) return;
    
    setIsSubmitting(true);

    try {
      // Get all participants of the conversation
      const { data: participants, error: participantsError } = await supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', conversationId);

      if (participantsError) throw participantsError;

      // For each participant, ensure conversation is visible
      for (const participant of participants) {
        const { data: visibilityRecord } = await supabase
          .from('conversation_visibility')
          .select('*')
          .eq('conversation_id', conversationId)
          .eq('user_id', participant.user_id)
          .maybeSingle();

        if (visibilityRecord?.status === 'hidden') {
          // Update visibility to visible
          await supabase
            .from('conversation_visibility')
            .update({ 
              status: 'visible',
              updated_at: new Date().toISOString() 
            })
            .eq('conversation_id', conversationId)
            .eq('user_id', participant.user_id);
        }
      }

      // Insert new message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          user_id: currentUser.id,
          text: newMessage.trim(),
          created_at: new Date().toISOString()
        });

      if (messageError) throw messageError;

      // Update conversation metadata
      const { error: updateError } = await supabase
        .from('conversations')
        .update({
          last_message: newMessage.trim(),
          last_updated: new Date().toISOString()
        })
        .eq('id', conversationId);

      if (updateError) throw updateError;

      // Send notification to other participant
      if (otherParticipant && otherParticipant.id !== currentUser.id) {
        try {
          // Get sender's name from profile
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('full_name, username')
            .eq('id', currentUser.id)
            .single();
          
          const senderName = senderProfile?.full_name || 
                            senderProfile?.username || 
                            currentUser?.user_metadata?.full_name || 
                            currentUser?.user_metadata?.name || 
                            'Someone';
          
          const notificationResult = await sendNewMessageNotification(
            conversationId,
            currentUser.id,
            otherParticipant.id,
            senderName,
            newMessage.trim()
          );
          
          if (!notificationResult.success) {
            console.warn('Failed to send message notification:', notificationResult.error);
          }
        } catch (notificationError) {
          console.error('Error sending message notification:', notificationError);
          // Don't fail message sending if notification fails
        }
      }

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      // Show temporary error message for send failure
      const tempErrorId = Date.now();
      setMessages(prev => [...prev, {
        id: `temp-error-${tempErrorId}`,
        conversation_id: conversationId,
        user_id: currentUser.id,
        text: newMessage.trim(),
        created_at: new Date().toISOString(),
        isErrorState: true
      }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If there's an error, show error message with retry option
  if (error) {
    return (
      <div className="chat-window-error">
        <p>{error}</p>
        <Button variant="secondary" onClick={handleRetry}>
          {t('buttons.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="chat-window">
      {/* Fixed Header */}
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-back-button-container">
            <CustomButton8 onClick={handleBack}>
              <FaArrowLeft />
              {otherUnreadMessages.size > 0 && (
                <div className="notification-dot pulse"></div>
              )}
            </CustomButton8>
          </div>

          {otherParticipant && (
            <div className="participant-info-chat">
              {otherParticipant.avatar_url && (
                <img
                  src={otherParticipant.avatar_url}
                  alt={t('chat.profile_picture')}
                  className="avatar-img-chat"
                />
              )}
              <div className="participant-name">
                <p className="username">{otherParticipant.username || t('chat.unknown_user')}</p>
                {occurrenceDetails && (
                  <p className="occurrence-date">
                    📅 {format(new Date(occurrenceDetails.occurrence_date), 'MMMM d, yyyy')} {' '}
                    🕐 {occurrenceDetails.occurrence_time.substring(0, 5)}
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className="actions-container">
            {conversationDetails?.posts && (
              <RideActionsV2 
                conversationId={conversationId}
                postId={conversationDetails.posts.id}
              />
            )}
            {conversationDetails?.package_id && (
              <PackageActionsV2 
                conversationId={conversationId}
                packageId={conversationDetails.package_id}
              />
            )}
            
            {/* User Actions Dropdown for reporting */}
            {otherParticipant && otherParticipant.id !== currentUser?.id && (
              <UserActionsDropdown
                userId={otherParticipant.id}
                userName={otherParticipant.username || 'User'}
                context={{
                  conversation_id: conversationId,
                  post_id: conversationDetails?.posts_id || null,
                  package_id: conversationDetails?.package_id || null
                }}
                size="medium"
                variant="minimal"
              />
            )}
            
            {/* Close Chat Button */}
            <CloseButton2
              size="small"
              onClick={toggleChat}
              className="chat-close-button"
            />
          </div>
        </div>
      </div>

      {/* Scrollable Messages Area */}
      <div className="chat-messages">
        {isLoading ? (
          <div className="chat-messages-loading">
            <Spinner size="medium" customText={t('chat.loading')} />
          </div>
        ) : (
          <>
            {messages.length === 0 ? (
              <div className="no-messages">
                <p>{t('chat.no_messages')}</p>
              </div>
            ) : (
              <>
                {messages.map(message => {
                  // Check if this is a system message
                  if (message.is_system_message || message.user_id === 'system') {
                    return (
                      <div key={message.id} className="message system-message">
                        <p>{message.text}</p>
                        <span className="timestamp">
                          {format(new Date(message.created_at), 'HH:mm')}
                        </span>
                      </div>
                    );
                  }
                  
                  // Regular user message - use is_from_current_user flag instead of comparing IDs
                  const isCurrentUser = message.is_from_current_user || message.user_id === currentUser?.id;
                  
                  return (
                    <div
                      key={message.id}
                      className={`message ${isCurrentUser ? 'sent' : 'received'} ${message.isErrorState ? 'error-state' : ''}`}
                    >
                      <p>{message.text}</p>
                      <span className="timestamp">
                        {message.isErrorState ? (
                          <span className="error-indicator">{t('chat.failed_to_send')}</span>
                        ) : (
                          format(new Date(message.created_at), 'HH:mm')
                        )}
                      </span>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="chat-footer">
        <ChatInput
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onSend={handleSendMessage}
          placeholder={t('chat.type_message')}
          disabled={isSubmitting}
          maxLength={2000}
        />
      </div>
    </div>
  );
};

export default ChatWindow;